import React, { useState, useEffect, useRef } from 'react';
import { Settings as SettingsIcon, Save, Send, RefreshCw, DollarSign, Calendar, Upload, Image, Building, Mail, Phone, MapPin, ChevronRight, Trash2, LayoutDashboard, Wallet } from 'lucide-react';
import toast from 'react-hot-toast';
import { settingsAPI, accountsAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import apiClient from '../lib/apiClient';

const Settings = () => {
  useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [settings, setSettings] = useState({
    telegram_bot_token: '',
    telegram_chat_id: '',
    telegram_enabled: false,
    exchange_rate_usd: '',
    exchange_rate_eur: '',
    exchange_rate_gbp: '',
    exchange_rate_gold: '',
    google_calendar_enabled: false,
    google_calendar_auto_sync: false,
    google_calendar_sync_interval: '24',
    company_name: '',
    company_tax_number: '',
    company_tax_office: '',
    address: '',
    city: '',
    country: 'Turkey',
    postal_code: '',
    email_info: '',
    email_support: '',
    email_sales: '',
    email_billing: '',
    phone_main: '',
    phone_support: '',
    phone_sales: '',
    phone_fax: '',
    dashboard_default_account: ''
  });
  const [loadingRates, setLoadingRates] = useState(false);
  const [logo, setLogo] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const fileInputRef = useRef(null);
  const [activeCategory, setActiveCategory] = useState('general');

  const categories = [
    { id: 'general', name: 'Genel Ayarlar', icon: SettingsIcon },
    { id: 'dashboard', name: 'Dashboard Ayarları', icon: LayoutDashboard },
    { id: 'company', name: 'Firma Bilgileri', icon: Building },
    { id: 'address', name: 'Adres Bilgileri', icon: MapPin },
    { id: 'emails', name: 'E-Posta Adresleri', icon: Mail },
    { id: 'phones', name: 'Telefon Numaraları', icon: Phone },
    { id: 'telegram', name: 'Telegram', icon: Send },
    { id: 'exchange', name: 'Kur Ayarları', icon: DollarSign },
    { id: 'calendar', name: 'Google Takvim', icon: Calendar }
  ];

  useEffect(() => {
    fetchSettings();
    loadLogo();
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    try {
      const data = await accountsAPI.getAll();
      setAccounts(data || []);
    } catch (error) {
      console.error('Hesaplar yüklenirken hata:', error);
    }
  };

  const loadLogo = async () => {
    try {
      const response = await apiClient.get('/settings/logo');
      const logoData = response.data;
      if (logoData && typeof logoData === 'string') {
        setLogoUrl(logoData);
      }
    } catch (error) {
      // Silently ignore errors
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Lütfen bir resim dosyası seçin');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'dan küçük olmalıdır');
      return;
    }

    setLogo(file);
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result;
      if (base64String && base64String.startsWith('data:image/')) {
        setLogoPreview(base64String);
      } else {
        toast.error('Logo işlenirken hata oluştu');
      }
    };
    reader.onerror = () => {
      toast.error('Logo okunurken hata oluştu');
    };
    reader.readAsDataURL(file);
  };

  const handleSaveLogo = async () => {
    if (!logoPreview) return;

    try {
      setUploadingLogo(true);
      toast.loading('Logo kaydediliyor...', { id: 'save-logo' });
      
      const response = await apiClient.post('/settings/logo', { logo: logoPreview });
      
      if (response.error) {
        throw new Error(response.error);
      }
      
      const uploadedLogoUrl = response.data?.url;
      
      if (!uploadedLogoUrl) {
        throw new Error('Logo URL boş döndü');
      }
      
      setLogoUrl(uploadedLogoUrl);
      setLogoPreview(null);
      setLogo(null);
      
      window.dispatchEvent(new CustomEvent('logoUpdated', { detail: { logo: uploadedLogoUrl } }));
      
      toast.success('Logo başarıyla kaydedildi', { id: 'save-logo', duration: 3000 });
    } catch (error) {
      toast.error('Logo kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'), { id: 'save-logo', duration: 5000 });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (window.confirm('Logoyu silmek istediğinize emin misiniz?')) {
      try {
        const response = await apiClient.delete('/settings/logo');
        
        if (response.error) {
          throw new Error(response.error);
        }
        
        setLogoUrl(null);
        setLogoPreview(null);
        setLogo(null);
        
        window.dispatchEvent(new CustomEvent('logoUpdated', { detail: { logo: null } }));
        
        toast.success('Logo başarıyla silindi');
      } catch (error) {
        toast.error('Logo silinemedi: ' + (error.message || 'Bilinmeyen hata'));
      }
    }
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const settingsData = await settingsAPI.getAll();
      setSettings({
        telegram_bot_token: settingsData.telegram_bot_token || '',
        telegram_chat_id: settingsData.telegram_chat_id || '',
        telegram_enabled: settingsData.telegram_enabled === '1' || settingsData.telegram_enabled === true || false,
        exchange_rate_usd: settingsData.exchange_rate_usd || settingsData.rate_usd || '32',
        exchange_rate_eur: settingsData.exchange_rate_eur || settingsData.rate_eur || '35',
        exchange_rate_gbp: settingsData.exchange_rate_gbp || settingsData.rate_gbp || '40',
        exchange_rate_gold: settingsData.exchange_rate_gold || settingsData.rate_gold || '2500',
        google_calendar_enabled: settingsData.google_calendar_enabled === '1' || settingsData.google_calendar_enabled === true || false,
        google_calendar_auto_sync: settingsData.google_calendar_auto_sync === '1' || settingsData.google_calendar_auto_sync === true || false,
        google_calendar_sync_interval: settingsData.google_calendar_sync_interval || '24',
        company_name: settingsData.company_name || '',
        company_tax_number: settingsData.company_tax_number || '',
        company_tax_office: settingsData.company_tax_office || '',
        address: settingsData.address || '',
        city: settingsData.city || '',
        country: settingsData.country || 'Turkey',
        postal_code: settingsData.postal_code || '',
        email_info: settingsData.email_info || '',
        email_support: settingsData.email_support || '',
        email_sales: settingsData.email_sales || '',
        email_billing: settingsData.email_billing || '',
        phone_main: settingsData.phone_main || '',
        phone_support: settingsData.phone_support || '',
        phone_sales: settingsData.phone_sales || '',
        phone_fax: settingsData.phone_fax || '',
        dashboard_default_account: settingsData.dashboard_default_account || ''
      });
    } catch (error) {
      console.error('Ayarlar getirme hatası:', error);
      toast.error('Ayarlar yüklenemedi: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const getCategoryKeys = (category) => {
    const keyMap = {
      general: ['company_name', 'company_tax_number', 'company_tax_office'],
      dashboard: ['dashboard_default_account'],
      company: ['company_name', 'company_tax_number', 'company_tax_office'],
      address: ['address', 'city', 'country', 'postal_code'],
      emails: ['email_info', 'email_support', 'email_sales', 'email_billing'],
      phones: ['phone_main', 'phone_support', 'phone_sales', 'phone_fax'],
      telegram: ['telegram_bot_token', 'telegram_chat_id', 'telegram_enabled'],
      exchange: ['exchange_rate_usd', 'exchange_rate_eur', 'exchange_rate_gbp', 'exchange_rate_gold'],
      calendar: ['google_calendar_enabled', 'google_calendar_auto_sync', 'google_calendar_sync_interval']
    };
    return keyMap[category] || [];
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    try {
      setSaving(true);
      
      const categoryKeys = getCategoryKeys(activeCategory);
      
      const savePromises = categoryKeys.map(key => {
        const value = settings[key];
        return settingsAPI.set(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
      });
      
      await Promise.all(savePromises);
      
      toast.success('Ayarlar başarıyla kaydedildi');
    } catch (error) {
      console.error('Ayar kaydetme hatası:', error);
      toast.error('Ayarlar kaydedilemedi: ' + (error.message || 'Bilinmeyen hata'));
    } finally {
      setSaving(false);
    }
  };

  const handleFetchExchangeRates = async () => {
    try {
      setLoadingRates(true);
      toast.loading('Kurlar alınıyor...', { id: 'fetch-rates' });
      
      const tcmbUrl = 'https://www.tcmb.gov.tr/kurlar/today.xml';
      
      let usdRate = null;
      let eurRate = null;
      let gbpRate = null;
      
      try {
        const tcmbResponse = await fetch(tcmbUrl);
        if (tcmbResponse.ok) {
          const xmlText = await tcmbResponse.text();
          
          const usdMatch = xmlText.match(/<Currency.*?CurrencyCode="USD".*?<ForexBuying>([\d,]+)<\/ForexBuying>/);
          if (usdMatch) {
            usdRate = parseFloat(usdMatch[1].replace(',', '.'));
            usdRate = Math.round(usdRate * 100) / 100;
          }
          
          const eurMatch = xmlText.match(/<Currency.*?CurrencyCode="EUR".*?<ForexBuying>([\d,]+)<\/ForexBuying>/);
          if (eurMatch) {
            eurRate = parseFloat(eurMatch[1].replace(',', '.'));
            eurRate = Math.round(eurRate * 100) / 100;
          }
          
          const gbpMatch = xmlText.match(/<Currency.*?CurrencyCode="GBP".*?<ForexBuying>([\d,]+)<\/ForexBuying>/);
          if (gbpMatch) {
            gbpRate = parseFloat(gbpMatch[1].replace(',', '.'));
            gbpRate = Math.round(gbpRate * 100) / 100;
          }
        }
      } catch (tcmbError) {
        try {
          const altResponse = await fetch('https://api.exchangerate-api.com/v4/latest/TRY');
          if (altResponse.ok) {
            const altData = await altResponse.json();
            if (altData.rates) {
              usdRate = 1 / (altData.rates.USD || 1);
              eurRate = 1 / (altData.rates.EUR || 1);
              gbpRate = 1 / (altData.rates.GBP || 1);
              usdRate = Math.round(usdRate * 100) / 100;
              eurRate = Math.round(eurRate * 100) / 100;
              gbpRate = Math.round(gbpRate * 100) / 100;
            }
          }
        } catch (altError) {
        }
      }
      
      const updatedSettings = { ...settings };
      let updated = false;
      
      if (usdRate && usdRate > 0) {
        updatedSettings.exchange_rate_usd = String(usdRate.toFixed(2));
        updated = true;
      }
      if (eurRate && eurRate > 0) {
        updatedSettings.exchange_rate_eur = String(eurRate.toFixed(2));
        updated = true;
      }
      if (gbpRate && gbpRate > 0) {
        updatedSettings.exchange_rate_gbp = String(gbpRate.toFixed(2));
        updated = true;
      }
      
      if (updated) {
        setSettings(updatedSettings);
        
        await Promise.all([
          usdRate && usdRate > 0 ? settingsAPI.set('exchange_rate_usd', String(usdRate.toFixed(2))) : Promise.resolve(),
          eurRate && eurRate > 0 ? settingsAPI.set('exchange_rate_eur', String(eurRate.toFixed(2))) : Promise.resolve(),
          gbpRate && gbpRate > 0 ? settingsAPI.set('exchange_rate_gbp', String(gbpRate.toFixed(2))) : Promise.resolve()
        ]);
        
        const successMessages = [];
        if (usdRate) successMessages.push(`USD: ${usdRate.toFixed(2)}`);
        if (eurRate) successMessages.push(`EUR: ${eurRate.toFixed(2)}`);
        if (gbpRate) successMessages.push(`GBP: ${gbpRate.toFixed(2)}`);
        
        toast.success('Kurlar güncellendi: ' + successMessages.join(', '), {
          id: 'fetch-rates',
          duration: 5000 
        });
      } else {
        toast.error('Kurlar alınamadı', {
          id: 'fetch-rates',
          duration: 5000 
        });
      }
      
    } catch (error) {
      console.error('Kur çekme hatası:', error);
      toast.error('Kur çekme hatası: ' + (error.message || 'Bilinmeyen hata'), {
        id: 'fetch-rates',
        duration: 5000 
      });
    } finally {
      setLoadingRates(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-2 md:p-3">
      {/* Header */}
      <div className="mb-3 md:mb-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Ayarlar</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1 md:mt-2 text-sm md:text-base">Uygulama ayarlarını yönetin</p>
      </div>

      {/* Main Layout with Sidebar */}
      <div className="flex flex-col md:flex-row gap-3 md:gap-4">
        {/* Sidebar */}
        <div className="w-full md:w-48 flex-shrink-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden sticky top-24">
            <div className="divide-y divide-gray-200 dark:divide-gray-700">
              {categories.map((category) => {
                const Icon = category.icon;
                const isActive = activeCategory === category.id;
                
                return (
                  <button
                    key={category.id}
                    onClick={() => setActiveCategory(category.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                      isActive
                        ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-600 text-blue-600 dark:text-blue-400'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-4 w-4" />
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                    {isActive && <ChevronRight className="h-4 w-4" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-w-0">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 md:p-5">
            {activeCategory === 'general' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Genel Ayarlar</h2>
                </div>

                {/* Logo Section */}
                <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Image className="h-5 w-5" />
                    Logo
                  </h3>
                  
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="h-24 w-24 bg-gray-100 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {logoPreview || logoUrl ? (
                        <img src={logoPreview || logoUrl} alt="Logo" className="h-full w-full object-contain" />
                      ) : (
                        <Image className="h-12 w-12 text-gray-400" />
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {logoUrl ? 'Logo yüklü' : 'Logo yüklenmedi'}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-3">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/gif,image/webp"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Logo Seç
                    </button>
                    {(logoPreview || logoUrl) && (
                      <>
                        <button
                          type="button"
                          onClick={handleSaveLogo}
                          disabled={uploadingLogo || !logo}
                          className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                        >
                          {uploadingLogo ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                              Kaydediliyor...
                            </>
                          ) : (
                            <>
                              <Save className="h-4 w-4 mr-2" />
                              Kaydet
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={handleRemoveLogo}
                          className="flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Sil
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Company Info */}
                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Building className="h-5 w-5" />
                    Firma Bilgileri
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Firma Adı
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={settings.company_name}
                      onChange={handleInputChange}
                      placeholder="Şirket A.Ş."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vergi Numarası
                      </label>
                      <input
                        type="text"
                        name="company_tax_number"
                        value={settings.company_tax_number}
                        onChange={handleInputChange}
                        placeholder="1234567890"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vergi Dairesi
                      </label>
                      <input
                        type="text"
                        name="company_tax_office"
                        value={settings.company_tax_office}
                        onChange={handleInputChange}
                        placeholder="Vergi Dairesi"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'dashboard' && (
              <div className="space-y-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Dashboard Ayarları</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Dashboard'da gelir/gider hesaplamaları için varsayılan cari hesabı seçin.
                  </p>
                </div>

                <div className="space-y-3">
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <Wallet className="h-5 w-5" />
                    Varsayılan Cari Hesap
                  </h3>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Hesap Seçin
                    </label>
                    <select
                      name="dashboard_default_account"
                      value={settings.dashboard_default_account}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Tüm Hesaplar</option>
                      {accounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.accountName} ({account.currency})
                        </option>
                      ))}
                    </select>
                    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {settings.dashboard_default_account ?
                        'Seçilen hesaptaki işlemler Dashboard "Son 6 Ay" grafiğinde gösterilecektir.' :
                        'Hesap seçilmediğinde tüm hesapların işlemleri gösterilir.'}
                    </p>
                    {accounts.length === 0 && (
                      <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg">
                        <p className="text-sm text-yellow-800 dark:text-yellow-200">
                          ⚠️ Henüz hiç cari hesap oluşturulmadı. Lütfen önce Cari Takip sayfasından bir hesap ekleyin.
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'company' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Firma Bilgileri</h2>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Firma Adı
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={settings.company_name}
                      onChange={handleInputChange}
                      placeholder="Şirket A.Ş."
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vergi Numarası
                      </label>
                      <input
                        type="text"
                        name="company_tax_number"
                        value={settings.company_tax_number}
                        onChange={handleInputChange}
                        placeholder="1234567890"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Vergi Dairesi
                      </label>
                      <input
                        type="text"
                        name="company_tax_office"
                        value={settings.company_tax_office}
                        onChange={handleInputChange}
                        placeholder="Vergi Dairesi"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'address' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Adres Bilgileri</h2>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Açık Adres
                    </label>
                    <textarea
                      name="address"
                      value={settings.address}
                      onChange={handleInputChange}
                      placeholder="Mahalle, Sokak, No..."
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Şehir
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={settings.city}
                        onChange={handleInputChange}
                        placeholder="İstanbul"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ülke
                      </label>
                      <input
                        type="text"
                        name="country"
                        value={settings.country}
                        onChange={handleInputChange}
                        placeholder="Turkey"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Posta Kodu
                      </label>
                      <input
                        type="text"
                        name="postal_code"
                        value={settings.postal_code}
                        onChange={handleInputChange}
                        placeholder="34000"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'emails' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">E-Posta Adresleri</h2>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bilgi E-Postası
                    </label>
                    <input
                      type="email"
                      name="email_info"
                      value={settings.email_info}
                      onChange={handleInputChange}
                      placeholder="info@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Destek E-Postası
                    </label>
                    <input
                      type="email"
                      name="email_support"
                      value={settings.email_support}
                      onChange={handleInputChange}
                      placeholder="support@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Satış E-Postası
                    </label>
                    <input
                      type="email"
                      name="email_sales"
                      value={settings.email_sales}
                      onChange={handleInputChange}
                      placeholder="sales@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Muhasebe E-Postası
                    </label>
                    <input
                      type="email"
                      name="email_billing"
                      value={settings.email_billing}
                      onChange={handleInputChange}
                      placeholder="billing@example.com"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'phones' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Telefon Numaraları</h2>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ana Telefon
                    </label>
                    <input
                      type="tel"
                      name="phone_main"
                      value={settings.phone_main}
                      onChange={handleInputChange}
                      placeholder="+90 212 123 45 67"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Destek Telefonu
                    </label>
                    <input
                      type="tel"
                      name="phone_support"
                      value={settings.phone_support}
                      onChange={handleInputChange}
                      placeholder="+90 212 123 45 67"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Satış Telefonu
                    </label>
                    <input
                      type="tel"
                      name="phone_sales"
                      value={settings.phone_sales}
                      onChange={handleInputChange}
                      placeholder="+90 212 123 45 67"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Faks
                    </label>
                    <input
                      type="tel"
                      name="phone_fax"
                      value={settings.phone_fax}
                      onChange={handleInputChange}
                      placeholder="+90 212 123 45 68"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'telegram' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Telegram Ayarları</h2>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 mb-4">
                    <input
                      type="checkbox"
                      id="telegram_enabled"
                      name="telegram_enabled"
                      checked={settings.telegram_enabled}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="telegram_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Telegram'ı etkinleştir
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Bot Token
                    </label>
                    <input
                      type="text"
                      name="telegram_bot_token"
                      value={settings.telegram_bot_token}
                      onChange={handleInputChange}
                      placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Chat ID
                    </label>
                    <input
                      type="text"
                      name="telegram_chat_id"
                      value={settings.telegram_chat_id}
                      onChange={handleInputChange}
                      placeholder="123456789"
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'exchange' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Kur Ayarları</h2>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        USD Kuru
                      </label>
                      <input
                        type="number"
                        name="exchange_rate_usd"
                        value={settings.exchange_rate_usd}
                        onChange={handleInputChange}
                        step="0.0001"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        EUR Kuru
                      </label>
                      <input
                        type="number"
                        name="exchange_rate_eur"
                        value={settings.exchange_rate_eur}
                        onChange={handleInputChange}
                        step="0.0001"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        GBP Kuru
                      </label>
                      <input
                        type="number"
                        name="exchange_rate_gbp"
                        value={settings.exchange_rate_gbp}
                        onChange={handleInputChange}
                        step="0.0001"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Altın Fiyatı
                      </label>
                      <input
                        type="number"
                        name="exchange_rate_gold"
                        value={settings.exchange_rate_gold}
                        onChange={handleInputChange}
                        step="0.01"
                        min="0"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleFetchExchangeRates}
                    disabled={loadingRates}
                    className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {loadingRates ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Alınıyor...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2" />
                        Otomatik Güncelle
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}

            {activeCategory === 'calendar' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Google Takvim</h2>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-3 mb-4">
                    <input
                      type="checkbox"
                      id="google_calendar_enabled"
                      name="google_calendar_enabled"
                      checked={settings.google_calendar_enabled}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="google_calendar_enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Google Takvim'i etkinleştir
                    </label>
                  </div>

                  {settings.google_calendar_enabled && (
                    <>
                      <div className="flex items-center space-x-3 mb-4">
                        <input
                          type="checkbox"
                          id="google_calendar_auto_sync"
                          name="google_calendar_auto_sync"
                          checked={settings.google_calendar_auto_sync}
                          onChange={handleInputChange}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                        />
                        <label htmlFor="google_calendar_auto_sync" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Otomatik senkronizasyonu etkinleştir
                        </label>
                      </div>

                      {settings.google_calendar_auto_sync && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Senkronizasyon Aralığı (saat)
                          </label>
                          <select
                            name="google_calendar_sync_interval"
                            value={settings.google_calendar_sync_interval}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          >
                            <option value="1">1 Saat</option>
                            <option value="6">6 Saat</option>
                            <option value="12">12 Saat</option>
                            <option value="24">24 Saat</option>
                            <option value="168">1 Hafta</option>
                          </select>
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Kaydediliyor...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Kaydet
                      </>
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
